// D-067 shop health score and setup steps, as the console shows them (directory column, shop page,
// support desk, overview). The scoring itself is in @ady/contracts console-desk.ts.
import {
  HEALTH_MAX,
  HEALTH_PARTS,
  SETUP_STEPS,
  type HealthBand,
  type SetupStep,
  type ShopHealth,
} from "@ady/contracts";
import { i18n, t } from "@ady/i18n";
import { cx, Icon, type IconName } from "@ady/ui";

const BAND_CLS: Record<HealthBand | "none", string> = {
  healthy: "bg-success-bg text-success-fg",
  watch: "bg-warning-bg text-warning-fg",
  at_risk: "bg-danger-bg text-danger-fg",
  none: "bg-sunken text-fg-secondary",
};
const BAR_CLS: Record<HealthBand | "none", string> = {
  healthy: "bg-success-fg",
  watch: "bg-warning-fg",
  at_risk: "bg-danger-fg",
  none: "bg-fg-tertiary",
};

export const STEP_ICON: Record<SetupStep, IconName> = {
  store_live: "store",
  products: "box",
  verified: "shieldCheck",
  domain: "globe",
  channels: "chat",
  first_order: "orders",
  wallet: "wallet",
};

export const reasonText = (r: string) =>
  i18n.tDynamic(`desk.health.reasons.${r}`, "desk.health.reasons.paused");

/** "82 Healthy" as a small pill. */
export function HealthChip({ health, compact = false }: { health: ShopHealth; compact?: boolean }) {
  const band = health.band ?? "none";
  return (
    <span
      className={cx(
        "inline-flex w-fit items-center gap-1 rounded-full px-1.5 py-px text-2xs font-semibold tabular-nums",
        BAND_CLS[band],
      )}
      title={health.reasons.map(reasonText).join(" · ") || undefined}
    >
      <Icon name="pulse" className="size-3" />
      {health.band ? health.score : "–"}
      {compact ? null : <span className="font-medium">{t(`desk.health.bands.${band}`)}</span>}
    </span>
  );
}

/** The score, a bar per part and what pulls it down. */
export function HealthBreakdown({ health }: { health: ShopHealth }) {
  const band = health.band ?? "none";
  return (
    <div className="flex flex-col gap-2" data-testid="health-breakdown">
      <span className="flex items-baseline gap-2">
        <span className="text-title-sm font-bold tabular-nums">{health.score}</span>
        <span className="text-xs text-fg-tertiary">/ 100</span>
        <span className={cx("rounded-full px-1.5 py-px text-2xs font-semibold", BAND_CLS[band])}>
          {t(`desk.health.bands.${band}`)}
        </span>
      </span>
      <ul className="flex flex-col gap-1.5">
        {HEALTH_PARTS.map((k) => {
          const pct = (health.parts[k] / HEALTH_MAX[k]) * 100;
          return (
            <li key={k} className="flex items-center gap-2 text-xs">
              <span className="w-24 flex-none text-fg-secondary">
                {t(`desk.health.parts.${k}`)}
              </span>
              <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-sunken">
                <span
                  className={cx(
                    "block h-full",
                    pct >= 70
                      ? BAR_CLS.healthy
                      : pct >= 40
                        ? BAR_CLS.watch
                        : BAR_CLS[band === "none" ? "none" : "at_risk"],
                  )}
                  style={{ width: `${Math.max(3, pct)}%` }}
                />
              </span>
              <span className="w-10 flex-none text-end font-semibold tabular-nums">
                {health.parts[k]}/{HEALTH_MAX[k]}
              </span>
            </li>
          );
        })}
      </ul>
      {health.reasons.length ? (
        <span className="flex flex-wrap gap-1">
          {health.reasons.map((r) => (
            <span
              key={r}
              className="inline-flex items-center gap-1 rounded-full bg-sunken px-1.5 py-px text-2xs font-medium text-fg-secondary"
            >
              <Icon name="triangle" className="size-3" />
              {reasonText(r)}
            </span>
          ))}
        </span>
      ) : null}
      <p className="text-2xs text-fg-tertiary">{t("desk.health.how")}</p>
    </div>
  );
}

/** The seven setup steps as ticks. */
export function SetupSteps({
  done,
  compact = false,
}: {
  done: readonly SetupStep[];
  compact?: boolean;
}) {
  return (
    <ol className={cx("flex flex-wrap", compact ? "gap-1" : "gap-1.5")}>
      {SETUP_STEPS.map((s) => {
        const ok = done.includes(s);
        return (
          <li
            key={s}
            title={t(`desk.setup.steps.${s}`)}
            className={cx(
              "inline-flex items-center gap-1 rounded-full text-2xs font-medium",
              compact ? "size-6 justify-center" : "px-2 py-0.5",
              ok ? "bg-success-bg text-success-fg" : "bg-sunken text-fg-tertiary",
            )}
          >
            <Icon name={ok ? "check" : STEP_ICON[s]} className="size-3" />
            {compact ? (
              <span className="ady-sr-only">{t(`desk.setup.steps.${s}`)}</span>
            ) : (
              t(`desk.setup.steps.${s}`)
            )}
          </li>
        );
      })}
    </ol>
  );
}
